import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { track, EVENTS } from "../lib/events";
import { useSiteSettings } from "../lib/siteSettings";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export default function Footer() {
  const { t } = useI18n();
  const settings = useSiteSettings();
  const year = new Date().getFullYear();
  const phone = settings["contact.phone"] || "";
  const phoneHref = phone ? `tel:${phone.replace(/[^+\d]/g, "")}` : null;
  const socials = [
    { key: "facebook", url: settings["social.facebook_url"], label: "Facebook", Icon: Facebook },
    { key: "instagram", url: settings["social.instagram_url"], label: "Instagram", Icon: Instagram },
    { key: "linkedin", url: settings["social.linkedin_url"], label: "LinkedIn", Icon: Linkedin },
    { key: "tiktok", url: settings["social.tiktok_url"], label: "TikTok", Icon: TikTokIcon },
  ].filter((s) => (s.url || "").trim());
  return (
    <footer className="mt-24 border-t border-pierre bg-lin" data-testid="site-footer">
      <div className="container-editorial py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <p className="eyebrow mb-4">Briouse</p>
          <h4 className="font-serif text-2xl mb-4">{t("footer.about")}</h4>
          <p className="text-ardoise/80 max-w-md leading-relaxed">{t("footer.about_body")}</p>
        </div>
        <div>
          <p className="eyebrow mb-4">{t("footer.contact")}</p>
          <address className="not-italic text-sm space-y-2 text-ardoise/80">
            <div>{settings["contact.address_fr"] || t("footer.address")}</div>
            {phoneHref && (
              <div>
                <a
                  href={phoneHref}
                  data-testid="footer-phone-link"
                  onClick={() => track(EVENTS.CLICK_PHONE, { source: "footer" })}
                  className="link-underline"
                >
                  {phone}
                </a>
              </div>
            )}
          </address>
          {socials.length > 0 && (
            <div className="flex items-center gap-3 mt-4" data-testid="footer-socials">
              {socials.map(({ key, url, label, Icon }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                   aria-label={label} title={label}
                   data-testid={`footer-social-${key}`}
                   className="text-ardoise/60 hover:text-domaine transition-colors">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="eyebrow mb-4">Navigation</p>
          <ul className="text-sm space-y-2 text-ardoise/80">
            <li><Link to="/" className="hover:text-domaine" data-testid="footer-home-link">{t("nav.home")}</Link></li>
            <li><Link to="/mariages" className="hover:text-domaine" data-testid="footer-mariages-link">{t("nav.celebrer")}</Link></li>
            <li><Link to="/evenements-prives" className="hover:text-domaine" data-testid="footer-evenements-link">Événements & Privatisations</Link></li>
          </ul>
          <p className="mt-4">
            <Link to="/extranet" className="text-[10px] uppercase tracking-widest text-ardoise/40 hover:text-ardoise/70 transition-colors" data-testid="footer-extranet-link">Accès équipe</Link>
          </p>
        </div>
      </div>
      <div className="border-t border-pierre">
        <div className="container-editorial py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-ardoise/60">
          <div>{t("footer.copyright").replace("{year}", year)}</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
            <Link to="/mentions-legales" className="hover:text-domaine" data-testid="footer-legal-link">{t("footer.legal")}</Link>
            <Link to="/politique-de-confidentialite" className="hover:text-domaine" data-testid="footer-privacy-link">{t("footer.privacy")}</Link>
            <Link to="/conditions-generales-de-vente" className="hover:text-domaine" data-testid="footer-cgv-link">CGV</Link>
            <button onClick={() => import("../lib/consent").then((m) => m.openCookieSettings())}
                    className="hover:text-domaine" data-testid="footer-cookies-btn">
              Gérer les cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
